import type {
  components,
  operations,
} from '@schuelerhilfe/shared-components/src/types/schema';
import * as Sentry from '@sentry/nextjs';
import { cache } from 'react';
import { SentryMessageCategory, withSentryCategory } from '@/features/sentry';
import { fetchClient } from '@/lib/apiClient';
import { cityCacheTag } from '@/lib/pageCacheTags';

// NOTE: this module is bundled into the client (e.g. form widgets import
// `submitForm`), so it must stay free of server secrets. Backend calls that
// need the `x-api-key` secret live in `lib/api.server.ts`.

// --- Schema-derived response shapes ---------------------------------------

export type LocationDto = components['schemas']['LocationDTO'];
export type CityDto = components['schemas']['CityDTO'];
export type DomainSettingsDto = components['schemas']['DomainSettingsDto'];
export type LocationSearchResultDto =
  components['schemas']['LocationSearchResultDTO'];
export type OfferLocationsDto = components['schemas']['OfferLocationsDTO'];
export type OfferLocationsOfferDto =
  components['schemas']['OfferLocationsOfferDTO'];
export type OfferLocationDto = components['schemas']['OfferLocationDTO'];

// `BlockDtoSchema` from the OpenAPI schema only types the two known
// fields (componentType, id). Real blocks carry component-specific
// extras; clients narrow on `componentType`. Add an index signature
// here so accessing those fields doesn't fail at compile time.
export type BlockDto = components['schemas']['BlockDtoSchema'] & {
  [key: string]: unknown;
};

export type ResolvedBlock = components['schemas']['ResolvedBlockDtoSchema'] & {
  [key: string]: unknown;
};

// `metadata` is intentionally untyped at the schema level (varies per
// content type). The FE knows which fields apply for the content type
// it's rendering, so we narrow the structural type locally.
export interface StrapiEntityResponse extends Omit<
  components['schemas']['LoadedEntityDto'],
  'metadata' | 'blocks'
> {
  metadata: {
    title?: string;
    slug?: string;
    heroImage?: { url?: string; [key: string]: unknown };
    readTimeMinutes?: number;
    [key: string]: unknown;
  };
  blocks: BlockDto[];
}

export type RenderPageResponse = components['schemas']['RenderPageResponseDto'];

// Request shape derived from the operation's requestBody.
export type RenderPageRequest = NonNullable<
  operations['RenderPageController_render']['requestBody']
>['content']['application/json'];

export type StrapiContentType = 'page' | 'magazine-article' | 'content-snippet';

export type MagazineArticleTeaser =
  components['schemas']['MagazineArticleTeaserDto'];
export type MagazineArticleList =
  components['schemas']['MagazineArticleListDto'];
export type MagazineCategory = components['schemas']['MagazineCategoryDto'];
export type MagazineArticle = components['schemas']['MagazineArticleDto'];
export type MagazineAuthor = components['schemas']['MagazineAuthorDto'];

// --- API calls (all going through fetchClient) ----------------------------

/**
 * The backend answered 404: there is no such city. Distinct from every other
 * failure because only this one may become a `notFound()` — a 404 over a
 * transient 5xx would deindex a live city URL.
 */
export class CityNotFoundError extends Error {
  constructor(readonly slug: string) {
    super(`City "${slug}" does not exist`);
    this.name = 'CityNotFoundError';
  }
}

export const getCityBySlug = cache(async (slug: string): Promise<CityDto> => {
  const { data, error, response } = await fetchClient.GET('/cities/{slug}', {
    params: { path: { slug } },
    next: { revalidate: 60, tags: [cityCacheTag(slug)] },
  });
  // Not reported to Sentry: a request for a city that does not exist is a
  // crawler or a stale link, not a backend problem.
  if (response?.status === 404) {
    throw new CityNotFoundError(slug);
  }
  if (error || !data) {
    const err = new Error(
      withSentryCategory(
        SentryMessageCategory.WEBSITE_BACKEND_CONNECTION,
        `Failed to fetch city "${slug}"`,
      ),
    );
    Sentry.captureException(err, { tags: { endpoint: 'cities', slug } });
    throw err;
  }
  return data;
});

export interface ListMagazineArticlesParams {
  locale: string;
  domain: string;
  category?: string | undefined;
  author?: string | undefined;
  /** The Article Card's featured article, left out of every batch. */
  exclude?: string | undefined;
  limit: number;
  offset: number;
}

/**
 * The magazine article grid's later batches — the first one is server-rendered
 * inside `POST /render-page` and never goes through HTTP (spec 10.02.05 § AC 4).
 *
 * One function for both sides: `lib/apiClient.ts` already resolves the internal
 * `API_URL` on the server and the public `NEXT_PUBLIC_API_URL` in the browser,
 * and `/magazine/articles` carries no API-key guard (50.04.15 § Security), so a
 * second server-only copy would differ only in the header it does not need.
 *
 * `no-store` for the same reason `renderPage` is: a magazine route already
 * makes an uncacheable `/render-page` call, so caching the articles beside it
 * would only serve a stale grid under a fresh Page.
 */
export async function listMagazineArticles(
  params: ListMagazineArticlesParams,
): Promise<MagazineArticleList> {
  const { data, error } = await fetchClient.GET('/api/magazine/articles', {
    params: { query: params },
    cache: 'no-store',
  });
  if (error || !data) {
    const err = new Error(
      withSentryCategory(
        SentryMessageCategory.WEBSITE_BACKEND_CONNECTION,
        'Failed to fetch magazine articles',
      ),
    );
    Sentry.captureException(err, {
      tags: { endpoint: 'api/magazine/articles' },
    });
    throw err;
  }
  return data;
}

export type FormSubmission =
  operations['FormsController_submit']['requestBody']['content']['application/json'];

/**
 * Browser-side fetch of a full published location by slug, via the public
 * `GET /api/locations/{slug}` endpoint (unlike `api.server.ts`'s
 * `getLocationBySlug`, which uses the API-key-guarded `/locations/{slug}`).
 * Used by `<AdjustableContactForm />` to resolve the ZipCity-selected
 * Standort for the `{confirmationLocation.*}` confirmation placeholders.
 * Returns null on any failure — the confirmation entity is a nice-to-have
 * and must never block or fail a submission.
 */
export async function getPublicLocationBySlug(
  slug: string,
): Promise<LocationDto | null> {
  try {
    const { data, response } = await fetchClient.GET('/api/locations/{slug}', {
      params: { path: { slug } },
    });
    if (!data || response.status !== 200) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export type AdjustableContactFormSubmission =
  operations['AdjustableContactFormsController_submit']['requestBody']['content']['application/json'];

// Task 9's `/api/forms/dynamic` — same submission pipeline as `/api/forms`,
// discriminated by `kind` instead of `type`. Mirrors `submitForm()`'s
// error-handling/Sentry pattern exactly (see comments there for rationale).
export async function submitAdjustableContactForm(
  body: AdjustableContactFormSubmission,
): Promise<void> {
  let result;
  try {
    result = await fetchClient.POST('/api/forms/dynamic', {
      body,
    });
  } catch (err) {
    // `err` here is typically a native Error (e.g. a network failure) —
    // JSON.stringify(err) serializes to '{}' since Error's message/stack
    // aren't own enumerable properties, so use its message instead.
    const detail = err instanceof Error ? err.message : JSON.stringify(err);
    const exception = new Error(
      withSentryCategory(
        SentryMessageCategory.FORMS_SUBMISSION,
        `Adjustable contact form submission failed: ${detail}`,
      ),
    );
    Sentry.captureException(exception, { tags: { formKind: body.kind } });
    throw err;
  }
  const { error, response } = result;

  if (error) {
    const err = new Error(
      withSentryCategory(
        SentryMessageCategory.FORMS_SUBMISSION,
        `Adjustable contact form submission failed: ${JSON.stringify(error)}`,
      ),
    );
    Sentry.captureException(err, { tags: { formKind: body.kind } });
    throw err;
  }
  if (!response) {
    const err = new Error(
      withSentryCategory(
        SentryMessageCategory.FORMS_SUBMISSION,
        'Adjustable contact form submission failed: no response received',
      ),
    );
    Sentry.captureException(err, { tags: { formKind: body.kind } });
    throw err;
  }
  if (response.status !== 204) {
    const err = new Error(
      withSentryCategory(
        SentryMessageCategory.FORMS_SUBMISSION,
        `Adjustable contact form submission failed: expected 204, got ${response.status}`,
      ),
    );
    Sentry.captureException(err, {
      tags: { formKind: body.kind },
      extra: { status: response.status },
    });
    throw err;
  }
}

export async function submitForm(body: FormSubmission): Promise<void> {
  let result;
  try {
    result = await fetchClient.POST('/api/forms', {
      body,
    });
  } catch (err) {
    // `err` here is typically a native Error (e.g. a network failure) —
    // JSON.stringify(err) serializes to '{}' since Error's message/stack
    // aren't own enumerable properties, so use its message instead.
    const detail = err instanceof Error ? err.message : JSON.stringify(err);
    const exception = new Error(
      withSentryCategory(
        SentryMessageCategory.FORMS_SUBMISSION,
        `Form submission failed: ${detail}`,
      ),
    );
    Sentry.captureException(exception, { tags: { formType: body.type } });
    throw err;
  }
  const { error, response } = result;

  if (error) {
    const err = new Error(
      withSentryCategory(
        SentryMessageCategory.FORMS_SUBMISSION,
        `Form submission failed: ${JSON.stringify(error)}`,
      ),
    );
    Sentry.captureException(err, { tags: { formType: body.type } });
    throw err;
  }
  if (!response) {
    const err = new Error(
      withSentryCategory(
        SentryMessageCategory.FORMS_SUBMISSION,
        'Form submission failed: no response received',
      ),
    );
    Sentry.captureException(err, { tags: { formType: body.type } });
    throw err;
  }
  if (response.status !== 204) {
    const err = new Error(
      withSentryCategory(
        SentryMessageCategory.FORMS_SUBMISSION,
        `Form submission failed: expected 204, got ${response.status}`,
      ),
    );
    Sentry.captureException(err, {
      tags: { formType: body.type },
      extra: { status: response.status },
    });
    throw err;
  }
}

export type NewsletterSubscription =
  operations['NewsletterController_subscribe']['requestBody']['content']['application/json'];

/**
 * Subscribes an address to the newsletter through the backend's Optimizely
 * Campaign proxy. Fire-and-forget on the backend side, so a rejection here is
 * the only signal the visitor gets — the caller surfaces it inline.
 */
export async function subscribeNewsletter(
  body: NewsletterSubscription,
): Promise<void> {
  let result;
  try {
    result = await fetchClient.POST('/api/newsletter/subscribe', { body });
  } catch (err) {
    // Same reasoning as `submitForm`: a native Error serializes to '{}' via
    // JSON.stringify, so use its message.
    const detail = err instanceof Error ? err.message : JSON.stringify(err);
    Sentry.captureException(
      new Error(
        withSentryCategory(
          SentryMessageCategory.NEWSLETTER_SUBSCRIPTION,
          `Newsletter subscription failed: ${detail}`,
        ),
      ),
    );
    throw err;
  }

  const { error, response } = result;

  if (error) {
    const err = new Error(
      withSentryCategory(
        SentryMessageCategory.NEWSLETTER_SUBSCRIPTION,
        `Newsletter subscription failed: ${JSON.stringify(error)}`,
      ),
    );
    Sentry.captureException(err);
    throw err;
  }
  if (response?.status !== 204) {
    const err = new Error(
      withSentryCategory(
        SentryMessageCategory.NEWSLETTER_SUBSCRIPTION,
        `Newsletter subscription failed: expected 204, got ${response?.status}`,
      ),
    );
    Sentry.captureException(err, { extra: { status: response?.status } });
    throw err;
  }
}
